// =============================================================================
// src/utils/listingCategories.ts
// Single source of truth for per-vertical listing fields.
// Consumed by: ListingEditor (inputs), PortfolioTours (card badges),
// PropertyInfoContent (viewer display) and usePublicTourFilters (portfolio filters).
// A test (listingCategories.test.ts) blocks drift between these surfaces.
// real_estate uses the typed columns on `listing` (handled by the legacy
// real-estate UI), so it has no attribute field defs here.
// =============================================================================

import type {
  ListingAttributes,
  ListingCategory,
} from "@/types/tour/tour.types";
import { formatCurrency } from "@u/currency";

export type FieldKind = "text" | "number" | "select" | "bool" | "time";

export interface ListingFieldDef {
  /** Key inside `listing.attributes`. */
  key: string;
  kind: FieldKind;
  /** i18n key for the field label. */
  i18nLabel: string;
  /** select-only: option values. */
  options?: readonly string[];
  /** select-only: i18n prefix for option labels (`${prefix}_${value}`). Omit to show the raw value. */
  optionPrefix?: string;
  /** text/number/time input placeholder (raw string, not i18n). Defaults to the label. */
  placeholder?: string;
  /** Show this field as a portfolio sub-filter for its category. */
  filterable?: boolean;
  /** Show this field as a badge/feature on the portfolio card. */
  card?: boolean;
}

/** Order matters: drives the category <select> and matches the 5 landing use-case cards (+ other). */
export const LISTING_CATEGORIES: readonly ListingCategory[] = [
  "real_estate",
  "hotel",
  "museum",
  "business",
  "education",
  "other",
] as const;

export const CATEGORY_FIELDS: Record<ListingCategory, readonly ListingFieldDef[]> = {
  real_estate: [], // typed columns + legacy real-estate UI
  hotel: [
    { key: "stars", kind: "number", i18nLabel: "listing_attr_stars", placeholder: "0", filterable: true, card: true },
    { key: "room_count", kind: "number", i18nLabel: "listing_attr_room_count", placeholder: "0" },
    { key: "check_in", kind: "time", i18nLabel: "listing_attr_check_in" },
    { key: "check_out", kind: "time", i18nLabel: "listing_attr_check_out" },
    { key: "price_per_night", kind: "number", i18nLabel: "listing_attr_price_per_night", placeholder: "0", card: true },
  ],
  museum: [
    { key: "open_time", kind: "time", i18nLabel: "listing_attr_open_time" },
    { key: "close_time", kind: "time", i18nLabel: "listing_attr_close_time" },
    { key: "ticket_price", kind: "number", i18nLabel: "listing_attr_ticket_price", placeholder: "0", card: true },
    { key: "free_entry", kind: "bool", i18nLabel: "listing_attr_free_entry", filterable: true, card: true },
    { key: "collection_type", kind: "text", i18nLabel: "listing_attr_collection_type" },
  ],
  business: [
    { key: "business_type", kind: "select", i18nLabel: "listing_attr_business_type", options: ["restaurant", "retail", "services", "other"], optionPrefix: "listing_business_type", filterable: true, card: true },
    { key: "open_time", kind: "time", i18nLabel: "listing_attr_open_time" },
    { key: "close_time", kind: "time", i18nLabel: "listing_attr_close_time" },
    { key: "price_range", kind: "select", i18nLabel: "listing_attr_price_range", options: ["1", "2", "3", "4"], optionPrefix: "listing_price_range", filterable: true, card: true },
  ],
  education: [
    { key: "level", kind: "select", i18nLabel: "listing_attr_level", options: ["primary", "secondary", "university", "other"], optionPrefix: "listing_level", filterable: true, card: true },
    { key: "public_private", kind: "select", i18nLabel: "listing_attr_public_private", options: ["public", "private"], optionPrefix: "listing_public_private" },
    { key: "programs_count", kind: "number", i18nLabel: "listing_attr_programs_count" },
    { key: "price_range", kind: "select", i18nLabel: "listing_attr_price_range", options: ["1", "2", "3", "4"], optionPrefix: "listing_price_range", filterable: true, card: true },
  ],
  other: [],
};

export function categoryFields(cat: ListingCategory): readonly ListingFieldDef[] {
  return CATEGORY_FIELDS[cat] ?? [];
}

export function filterableFields(cat: ListingCategory): ListingFieldDef[] {
  return categoryFields(cat).filter((f) => f.filterable);
}

export function cardFields(cat: ListingCategory): ListingFieldDef[] {
  return categoryFields(cat).filter((f) => f.card);
}

/** Read a single attribute value safely off a listing.attributes bag. */
export function attrValue(
  attributes: ListingAttributes | null | undefined,
  key: string,
): string | number | boolean | null {
  if (!attributes) return null;
  const v = attributes[key];
  return v === undefined ? null : v;
}

/** True when the value is present (non-null, non-empty-string). */
export function hasAttr(
  attributes: ListingAttributes | null | undefined,
  key: string,
): boolean {
  const v = attrValue(attributes, key);
  return v !== null && v !== "";
}

export interface FormatAttrOpts {
  t: (key: string, opts?: Record<string, unknown>) => string;
  lang: string;
  currency?: string;
}

/**
 * Human-readable display string for one attribute value, category-aware.
 * Pure given `t` (injected) — used by both the portfolio card and the viewer.
 */
export function formatAttr(
  def: ListingFieldDef,
  value: string | number | boolean | null,
  { t, lang, currency = "EUR" }: FormatAttrOpts,
): string | null {
  if (value === null || value === "") return null;

  switch (def.key) {
    case "stars":
      return `${value}★`;
    case "price_per_night":
      return `${formatCurrency(Number(value), currency, lang)} ${t("listing_per_night_suffix")}`;
    case "ticket_price":
      return formatCurrency(Number(value), currency, lang);
    case "price_range":
      return "€".repeat(Math.max(1, Math.min(4, Number(value))));
    case "free_entry":
      return value ? t("listing_free_entry") : null;
    default:
      break;
  }

  if (def.kind === "bool") return value ? t("listing_bool_true") : t("listing_bool_false");
  if (def.kind === "select" && def.optionPrefix)
    return t(`${def.optionPrefix}_${value}`, { defaultValue: String(value) });
  return String(value);
}
